import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';
import authProfileService from '../../services/authProfileService';
import ProfileSelect from './ProfileSelect';
import './ScanConfigDialog.css';

const ScanConfigDialog = ({ initialConfig, projectId, onStart, onCancel }) => {
  const { darkMode } = useTheme();
  const [config, setConfig] = useState({
    scanName: initialConfig.scanName || '',
    scanType: initialConfig.scanType || 'all',
    insertionPoints: initialConfig.insertionPoints || ['url_params', 'body_params', 'headers'],
    timingProfile: 'normal',
    authProfileId: '',
    payloadFilter: {
      onlyEvasionTechniques: false,
      evasionTechniques: []
    },
    ...initialConfig
  });
  const [authProfiles, setAuthProfiles] = useState([]);

  const SCAN_TYPES = [
    { value: 'all', label: 'All Active Attacks' },
    { value: 'xss', label: 'XSS (Reflected)' },
    { value: 'stored_xss', label: 'XSS (Stored)' },
    { value: 'sqli', label: 'SQL Injection' },
    { value: 'nosqli', label: 'NoSQL Injection' },
    { value: 'rce', label: 'OS Command Injection' },
    { value: 'code_injection', label: 'Code Injection (eval/exec)' },
    { value: 'lfi', label: 'Path Traversal (LFI)' },
    { value: 'rfi', label: 'Remote File Inclusion (RFI)' },
    { value: 'ssti', label: 'Template Injection (SSTI)' },
    { value: 'xxe', label: 'XXE (XML External Entity)' },
    { value: 'xpath', label: 'XPath Injection' },
    { value: 'ldap', label: 'LDAP Injection' },
    { value: 'deserialization', label: 'Insecure Deserialization' },
    { value: 'ssrf', label: 'SSRF' },
    { value: 'csrf', label: 'CSRF' },
    { value: 'crlf', label: 'CRLF / Header Injection' },
    { value: 'open_redirect', label: 'Open Redirect' },
    { value: 'host_header', label: 'Host Header Injection' },
    { value: 'hpp', label: 'HTTP Parameter Pollution' },
    { value: 'padding_oracle', label: 'Padding Oracle' },
    { value: 'session_fixation', label: 'Session Fixation' },
    { value: 'http_smuggling', label: 'HTTP Request Smuggling' },
  ];

  const targetScope = initialConfig.targetScope || {};
  const targetCount = targetScope.type === 'urls'
    ? (targetScope.values || []).length
    : (targetScope.value ? 1 : 0);
  const targetSummary = targetScope.type === 'urls'
    ? `${targetCount} URL${targetCount === 1 ? '' : 's'} selected`
    : (targetScope.value || '');

  useEffect(() => {
    if (projectId) {
      authProfileService.getAll(projectId).then(setAuthProfiles).catch(() => {});
    }
  }, [projectId]);

  const handleTimingChange = (profile) => {
    const presets = {
      aggressive: { requestsPerSecond: 10, randomJitter: false, jitterRange: 0 },
      fast: { requestsPerSecond: 5, randomJitter: true, jitterRange: 0.3 },
      normal: { requestsPerSecond: 3, randomJitter: true, jitterRange: 0.5 },
      slow: { requestsPerSecond: 1, randomJitter: true, jitterRange: 0.7 },
      stealth: { requestsPerSecond: 0.2, randomJitter: true, jitterRange: 1.0 }
    };
    setConfig({ ...config, timingProfile: profile, timing: presets[profile] });
  };

  const handleInsertionPointToggle = (point) => {
    const points = config.insertionPoints.includes(point)
      ? config.insertionPoints.filter(p => p !== point)
      : [...config.insertionPoints, point];
    setConfig({ ...config, insertionPoints: points });
  };

  const handleEvasionToggle = (tech) => {
    const techniques = config.payloadFilter.evasionTechniques.includes(tech)
      ? config.payloadFilter.evasionTechniques.filter(t => t !== tech)
      : [...config.payloadFilter.evasionTechniques, tech];
    setConfig({
      ...config,
      payloadFilter: { ...config.payloadFilter, evasionTechniques: techniques }
    });
  };

  const handleStart = () => {
    onStart(config);
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    },
    dialog: {
      background: darkMode ? '#1f2937' : '#ffffff',
      borderRadius: '12px',
      padding: '28px',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '80vh',
      overflowY: 'auto',
      boxShadow: darkMode
        ? '0 20px 60px rgba(0, 0, 0, 0.6)'
        : '0 20px 60px rgba(0, 0, 0, 0.3)',
      border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: darkMode ? '2px solid #374151' : '2px solid #e5e7eb'
    },
    title: {
      fontSize: '20px',
      fontWeight: '700',
      color: darkMode ? '#f3f4f6' : '#111827',
      margin: 0
    },
    closeBtn: {
      background: 'transparent',
      border: 'none',
      color: darkMode ? '#9ca3af' : '#6b7280',
      cursor: 'pointer',
      fontSize: '20px',
      padding: '4px 8px',
      transition: 'color 0.2s'
    },
    section: {
      marginBottom: '24px'
    },
    label: {
      display: 'block',
      fontSize: '13px',
      fontWeight: '600',
      color: darkMode ? '#d1d5db' : '#374151',
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    input: {
      width: '100%',
      padding: '10px 14px',
      background: darkMode ? '#374151' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      border: darkMode ? '1px solid #4b5563' : '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s'
    },
    select: {
      width: '100%',
      padding: '10px 14px',
      background: darkMode ? '#374151' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      border: darkMode ? '1px solid #4b5563' : '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      fontFamily: 'inherit',
      cursor: 'pointer',
      transition: 'border-color 0.2s'
    },
    checkboxList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      color: darkMode ? '#d1d5db' : '#374151',
      fontSize: '14px',
      cursor: 'pointer'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    actions: {
      display: 'flex',
      gap: '12px',
      marginTop: '28px',
      paddingTop: '20px',
      borderTop: darkMode ? '2px solid #374151' : '2px solid #e5e7eb'
    },
    startBtn: {
      flex: 1,
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
    },
    cancelBtn: {
      padding: '12px 24px',
      background: darkMode ? '#374151' : '#f3f4f6',
      color: darkMode ? '#d1d5db' : '#374151',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    hint: {
      fontSize: '12px',
      color: darkMode ? '#9ca3af' : '#6b7280',
      marginTop: '6px',
      fontStyle: 'italic'
    }
  };

  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Configure Vulnerability Scan</h3>
          <button style={styles.closeBtn} onClick={onCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Scan Name</label>
          <input
            type="text"
            style={styles.input}
            value={config.scanName}
            onChange={(e) => setConfig({ ...config, scanName: e.target.value })}
            placeholder="Enter scan name"
          />
        </div>

        {targetSummary && (
          <div style={styles.section}>
            <label style={styles.label}>Target{targetCount > 1 ? 's' : ''}</label>
            <div style={{
              ...styles.input,
              maxHeight: '92px', overflowY: 'auto', whiteSpace: 'pre-wrap',
              wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '12px', cursor: 'default'
            }}>
              {targetScope.type === 'urls'
                ? (targetScope.values || []).join('\n')
                : targetSummary}
            </div>
            {targetCount > 1 && (
              <div style={styles.hint}>{targetCount} URLs will be scanned with the settings below.</div>
            )}
          </div>
        )}

        <div style={styles.section}>
          <label style={styles.label}>Scan Type</label>
          <select
            style={styles.select}
            value={config.scanType}
            onChange={(e) => setConfig({ ...config, scanType: e.target.value })}
          >
            {SCAN_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Authentication</label>
          <ProfileSelect
            profiles={authProfiles}
            value={config.authProfileId || ''}
            onChange={(id) => setConfig({ ...config, authProfileId: id })}
            darkMode={darkMode}
          />
          <div style={styles.hint}>
            Default is unauthenticated. Pick a profile to scan as that user (its cookies/auth are injected on every request).
          </div>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Timing Profile</label>
          <select
            style={styles.select}
            value={config.timingProfile}
            onChange={(e) => handleTimingChange(e.target.value)}
          >
            <option value="aggressive">Aggressive (10 req/s) - Fast but noisy</option>
            <option value="fast">Fast (5 req/s) - Quick scan</option>
            <option value="normal">Normal (3 req/s) - Balanced</option>
            <option value="slow">Slow (1 req/s) - Less detectable</option>
            <option value="stealth">Stealth (0.2 req/s) - Evades SIEM</option>
          </select>
          <div style={styles.hint}>
            Lower speeds reduce SIEM detection but take longer
          </div>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Injection Points</label>
          <div style={styles.checkboxList}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={config.insertionPoints.includes('url_params')}
                onChange={() => handleInsertionPointToggle('url_params')}
              />
              URL Parameters
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={config.insertionPoints.includes('body_params')}
                onChange={() => handleInsertionPointToggle('body_params')}
              />
              Body Parameters
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={config.insertionPoints.includes('headers')}
                onChange={() => handleInsertionPointToggle('headers')}
              />
              Headers
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={config.insertionPoints.includes('cookies')}
                onChange={() => handleInsertionPointToggle('cookies')}
              />
              Cookies
            </label>
          </div>
        </div>

        <div style={styles.section}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              style={styles.checkbox}
              checked={config.payloadFilter.onlyEvasionTechniques}
              onChange={(e) => setConfig({
                ...config,
                payloadFilter: { ...config.payloadFilter, onlyEvasionTechniques: e.target.checked }
              })}
            />
            Only WAF Bypass Payloads (Evasion Techniques)
          </label>
          {config.payloadFilter.onlyEvasionTechniques && (
            <div style={{ marginLeft: '28px', marginTop: '12px' }}>
              <div style={styles.checkboxList}>
                {['case_variation', 'encoding', 'comment_obfuscation', 'whitespace_variation', 'operator_variation'].map(tech => (
                  <label key={tech} style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={config.payloadFilter.evasionTechniques.includes(tech)}
                      onChange={() => handleEvasionToggle(tech)}
                    />
                    {tech.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={styles.actions}>
          <button
            style={styles.startBtn}
            onClick={handleStart}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #059669, #047857)';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #10b981, #059669)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
            }}
          >
            <FontAwesomeIcon icon={faPlay} /> Start Scan
          </button>
          <button
            style={styles.cancelBtn}
            onClick={onCancel}
            onMouseEnter={(e) => {
              e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScanConfigDialog;
